import { useState } from 'react';
import { Plus } from 'lucide-react';
import { formatAttendanceBreakdown, sumGuestAttendance } from '@peles/shared';
import { useClientEventGuests } from '../../hooks/usePortal';
import type { ClientGuest } from '../../api/portal';
import { ClientGuestModal } from './ClientGuestModal';

export interface ClientGuestListViewProps {
  eventId: string;
  /** Client permission — gates adding and editing guests, never viewing them. */
  canManageGuests?: boolean;
}

/** Keyed by the normalized RSVP response the portal returns. */
const RSVP_COLORS: Record<string, string> = {
  YES: 'bg-green-100 text-green-700',
  NO: 'bg-red-100 text-red-700',
  MAYBE: 'bg-yellow-100 text-yellow-700',
  PENDING: 'bg-gray-100 text-gray-600',
};

const RSVP_LABELS: Record<string, string> = {
  YES: 'Attending',
  NO: 'Not attending',
  MAYBE: 'Maybe',
  PENDING: 'Pending',
};

/**
 * The client's own guest list: how many people are actually coming, who
 * answered what, and how to reach them.
 *
 * Head counts are resolved server-side (`resolveGuestAttendance`), so this
 * view only adds them up — the numbers here, the RSVP report and the campaign
 * screens all come from the same resolution.
 *
 * Owns its Add/Edit modal, per the list-components-own-their-modals rule.
 */
export function ClientGuestListView({
  eventId,
  canManageGuests = false,
}: ClientGuestListViewProps) {
  const { data: guests, isLoading } = useClientEventGuests(eventId);
  const [guestModalOpen, setGuestModalOpen] = useState(false);
  const [editingGuest, setEditingGuest] = useState<ClientGuest | null>(null);

  const openAdd = () => {
    setEditingGuest(null);
    setGuestModalOpen(true);
  };

  const addButton = canManageGuests ? (
    <div className="flex justify-end">
      <button
        type="button"
        onClick={openAdd}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-white bg-primary-600 rounded-lg hover:bg-primary-700"
      >
        <Plus className="w-4 h-4" />
        Add Guest
      </button>
    </div>
  ) : null;

  const guestModal = canManageGuests ? (
    <ClientGuestModal
      open={guestModalOpen}
      onClose={() => setGuestModalOpen(false)}
      eventId={eventId}
      guest={editingGuest}
    />
  ) : null;

  if (isLoading) return <p className="text-gray-500 p-4">Loading...</p>;
  if (!guests || guests.length === 0)
    return (
      <div className="space-y-4">
        {addButton}
        <p className="text-gray-500 text-center py-8">No guests found.</p>
        {guestModal}
      </div>
    );

  const totals = sumGuestAttendance(
    guests.map((g) => ({
      response: g.rsvpResponse,
      adults: g.attendingAdults,
      kids: g.attendingKids,
      total: g.attendingTotal,
    })),
  );

  return (
    <div className="space-y-4">
      {addButton}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div
          data-testid="guest-attendants-summary"
          className="bg-primary-50 border border-primary-200 rounded-lg p-3 text-center"
        >
          <p className="text-2xl font-bold text-primary-700">{totals.attendants}</p>
          <p className="text-xs text-primary-700">Attendants</p>
          <p className="text-[11px] text-primary-600">
            {formatAttendanceBreakdown(totals.adults, totals.kids) || 'No one confirmed yet'}
          </p>
        </div>
        <div
          data-testid="guest-attending-summary"
          className="bg-green-50 border border-green-200 rounded-lg p-3 text-center"
        >
          <p className="text-2xl font-bold text-green-700">{totals.yes}</p>
          <p className="text-xs text-green-600">Attending</p>
        </div>
        <div
          data-testid="guest-not-attending-summary"
          className="bg-red-50 border border-red-200 rounded-lg p-3 text-center"
        >
          <p className="text-2xl font-bold text-red-700">{totals.no}</p>
          <p className="text-xs text-red-600">Not Attending</p>
        </div>
        <div
          data-testid="guest-pending-summary"
          className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center"
        >
          <p className="text-2xl font-bold text-gray-700">{totals.pending + totals.maybe}</p>
          <p className="text-xs text-gray-600">
            Pending{totals.maybe > 0 ? ` · ${totals.maybe} maybe` : ''}
          </p>
        </div>
      </div>

      <div className="bg-white rounded-lg border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3 hidden md:table-cell">Email</th>
              <th className="text-left p-3 hidden md:table-cell">Family Group</th>
              <th className="text-left p-3">RSVP</th>
              <th className="text-center p-3">Attendants</th>
              <th className="text-right p-3 hidden md:table-cell">Table</th>
              <th className="text-left p-3 hidden md:table-cell">Meal</th>
            </tr>
          </thead>
          <tbody>
            {guests.map((guest) => (
              <tr
                key={guest.id}
                className={`border-t ${canManageGuests ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                onClick={
                  canManageGuests
                    ? () => {
                        setEditingGuest(guest);
                        setGuestModalOpen(true);
                      }
                    : undefined
                }
              >
                <td className="p-3 font-medium">
                  <span className="block">
                    {guest.firstName} {guest.lastName || ''}
                  </span>
                  {/* The email column is hidden on phones — keep it on the row. */}
                  {guest.email && (
                    <span className="block md:hidden text-xs font-normal text-gray-500 break-all">
                      {guest.email}
                    </span>
                  )}
                </td>
                <td className="p-3 text-gray-600 hidden md:table-cell break-all">
                  {guest.email || '-'}
                </td>
                <td className="p-3 text-gray-600 hidden md:table-cell">
                  {guest.familyGroup || '-'}
                </td>
                <td className="p-3">
                  <span
                    className={`text-xs px-2 py-0.5 rounded font-medium ${RSVP_COLORS[guest.rsvpResponse] || 'bg-gray-100 text-gray-600'}`}
                  >
                    {RSVP_LABELS[guest.rsvpResponse] ?? guest.rsvpResponse}
                  </span>
                </td>
                <td className="p-3 text-center">
                  {guest.attendingTotal > 0 ? (
                    <>
                      <span className="font-medium text-gray-900">{guest.attendingTotal}</span>
                      <span className="block text-[11px] text-gray-500">
                        {formatAttendanceBreakdown(guest.attendingAdults, guest.attendingKids)}
                      </span>
                    </>
                  ) : (
                    <span className="text-gray-400">-</span>
                  )}
                </td>
                <td className="p-3 text-right hidden md:table-cell">
                  {guest.tableNumber != null ? `Table ${guest.tableNumber}` : '-'}
                </td>
                <td className="p-3 text-gray-600 hidden md:table-cell">
                  {guest.mealPreference || '-'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {guestModal}
    </div>
  );
}
